import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { tap, delay } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
   providedIn: 'root',
})
export class LeaderService {
   API_BASE_URL = environment.lTkApiUrl; // Replace with actual base URL

   // Cache properties
   private goalListCache: any;
   private hospitalListCache: any;
   private statusListCache: any;
   private taskListCache: any;
   private opsHospitalDataCache: { [key: string]: any } = {};
   private currentSnackBar: any; // Track current snackbar

   constructor(public http: HttpClient, private snackBar: MatSnackBar) {}

   // Get Goal List with Caching
   getGoalList(qtr?: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/summary${this.getQuarterPathSegment(qtr)}`);
   }

   // Get Hospital List with Caching
   getHospitalList(forceRefresh: boolean = false) {
      if (this.hospitalListCache && !forceRefresh) {
         return new BehaviorSubject(this.hospitalListCache).asObservable();
      }
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/hospitaldetails`).pipe(
         tap((res: any) => {
            this.hospitalListCache = res;
         })
      );
   }

   /** Clear the cached hospital list. Call after assignment / override changes. */
   clearHospitalListCache() {
      this.hospitalListCache = null;
   }
   /** GET v1/goalhospital/hospital/{hospitalId} — single hospital via dbo.usp_GetHospitalById */
   getHospitalById(hospitalId: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/hospital/${hospitalId}`);
   }

   getTaskLookUpData() {
      return this.http.get(`${this.API_BASE_URL}/v1/lookups?types=AssignedType,TaskCategory,TaskStatus`);
   }

   getGoalDetailsById(goalTargetId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/goaltarget/${goalTargetId}`);
   }
   postGoalActivity(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/goaltarget/postgoalactivity`, data);
   }

   getQtrList() {
      return this.http.get(`${this.API_BASE_URL}/v1/lookups/getquarters`);
   }

   getStatusList() {
      if (this.statusListCache) {
         return new BehaviorSubject(this.statusListCache).asObservable();
      }
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/statuslist`).pipe(
         tap((res: any) => {
            this.statusListCache = res;
         })
      );
   }

   // Get Task List with Caching
   getTaskList(qtr?: any, hospitalId?: number) {
      const quarter = this.getQuarterPathSegment(qtr);
      const params = hospitalId != null ? `?hospitalId=${hospitalId}` : '';
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/taskdetails${quarter}${params}`);
   }

   /**
    * Goals for a single hospital in a quarter. Shape per item:
    * { goalTargetId, goalName, quarter } — the hospital's own concrete goal target, which is what a
    * single-hospital task stores in affiliatedGoalId. Soft-deleted goals are excluded server-side.
    */
   getGoalDictionaryWithQtr(hospitalId: any, qtrId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/getgoalsfortask?practiceSKey=${hospitalId}&quarter=${qtrId}`);
   }

   /**
    * Union of goals across multiple hospitals for a quarter, with per-goal coverage counts.
    * Shape per item: { goalDefinitionId, goalName, hospitalsCovered, hospitalsTotal,
    * instances: [{ practiceSKey, goalTargetId }] }. goalDefinitionId identifies the goal across
    * all requested hospitals and is what a bulk task saves; instances only lists hospitals that
    * have the goal set up, so derive the gap from the requested ids.
    */
   getGoalsForTaskBulk(hospitalIds: number[], qtrId: any) {
      // GetGoalCoverages binds practiceSKeys as int[]? — ASP.NET Core needs repeated
      // ?practiceSKeys=1&practiceSKeys=2, not a comma-joined value.
      let params = new HttpParams().set('quarter', qtrId);
      (hospitalIds || []).forEach(id => { params = params.append('practiceSKeys', String(id)); });
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/getgoalcoverages`, { params });
   }

   /**
    * Resolves the mapped person per hospital per role (RM/DO/VP), for the "Assign to all"
    * checkboxes. Expected shape: [{ practiceSKey, role, assignee: { id, displayName, email } | null }].
    */
   getRoleAssignees(hospitalIds: number[], roles: string[]) {
      // Same array-binding convention as getgoalsfortask — repeated keys, not comma-joined,
      // until confirmed otherwise against the actual controller.
      let params = new HttpParams();
      (hospitalIds || []).forEach(id => { params = params.append('hospitalIds', String(id)); });
      (roles || []).forEach(r => { params = params.append('roles', r); });
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/getroleassignees`, { params });
   }

    getGoalDictionary() {
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/goal-dictionary`);
   }
   ltkuserrole(){
   return this.http.get(`${this.API_BASE_URL}/v1/user/ltk-user-role`);
  }
   getGoalsForHospitalTarget(practiceSKey: any, quarterId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/getgoalforhospitaltarget-new?practiceSKey=${practiceSKey}&quarterId=${quarterId}`);
   }

   getAssignedUserDictionary(hospitalId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/getassignedtoddl?hospitalId=${hospitalId}`);
   }

   /**
    * Single task for the modal. `quarter` scopes the lookup to that quarter — without
    * it the API falls back to the current quarter, which hides tasks belonging to a
    * past/future quarter. Omitted when blank or 'default' so the request stays
    * byte-identical to the pre-quarter call.
    */
   getTaskDetailsById(taskId: any, quarter?: string) {
      const params = new URLSearchParams();
      params.set('taskId', taskId);
      if (quarter && quarter.trim() && quarter.toLowerCase() !== 'default') {
         params.set('quarter', quarter);
      }
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/gettaskbyid?${params.toString()}`);
   }

  getcommentbytaskid(taskId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/getcommentbytaskid?taskId=${taskId}`);
   }
   createCommentByTask(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/ltktask/createcommentbytask`, data);
   }
   updateCommentByTask(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/ltktask/updatecommentbytask`, data);
   }
   deleteCommentByTask(data: any) {
      return this.http.request('DELETE', `${this.API_BASE_URL}/v1/ltktask/deletecommentbytask`, { body: data });
   }

   searchUserDataList(searchText: string) {
      return this.http.get(`${this.API_BASE_URL}/v1/user/assignee?namecontains=${searchText}`);
   }

   private userDictionaryCache: any;
   userDisctionary() {
      if (this.userDictionaryCache) {
         return new BehaviorSubject(this.userDictionaryCache).asObservable();
      }
      return this.http.get(`${this.API_BASE_URL}/v1/user/assignee`).pipe(
         tap((res: any) => {
            this.userDictionaryCache = res;
         })
      );
   }
   creticalHospitalUpdate(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/goalhospital/updateiscritical`, data);
   }
   getCriticalHospitals() {
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/getcriticalhospitals`);
   }

   gettasktaguserlist(taskId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/ltktask/gettagusersbytaskid?taskId=${taskId}`);
   }
   postTaskTagUserlist(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/ltktask/tagtask`, data);
   }
   reassignTask(data: any) {
      return this.http.patch(`${this.API_BASE_URL}/v1/ltktask/reassigntask`, data);
   }

   completeTask(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/ltktask/completetask`,data);
   }

   createScheduleMeeting(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/schedule/createmeeting`, data);
   }

   getTodayMeetings(){
      return this.http.get(`${this.API_BASE_URL}/v1/ltkdashboard/getmeetingsbydate`);
   }

   getScheduledMeetings(type: string, hospitalId?: number) {
      const params = hospitalId != null ? `?hospitalId=${hospitalId}` : '';
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/getscheduledmeetings/${type}${params}`);
   }

   getHospitalNotes(practiceSKey: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/hospitalnotes/hospital/${practiceSKey}`);
   }

   createHospitalNote(data: { practiceSKey: number; noteName: string; description: string; isHMVisible?: boolean }) {
      return this.http.post(`${this.API_BASE_URL}/v1/hospitalnotes`, data);
   }

   updateHospitalNote(data: { noteId: number; noteName: string; description: string; isHMVisible?: boolean }) {
      return this.http.put(`${this.API_BASE_URL}/v1/hospitalnotes`, data);
   }

   deleteHospitalNote(noteId: number) {
      return this.http.delete(`${this.API_BASE_URL}/v1/hospitalnotes/${noteId}`);
   }

   getOpsStatus(qtr: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/ltkdashboard/ops-status${this.getQuarterPathSegment(qtr)}`);
   }


   uploadCSV(file: File) {
      const formData = new FormData()
      formData.append('file', file, file.name);

      // observe: 'response' tells Angular to give us the full status, headers, etc.
      return this.http.post(`${this.API_BASE_URL}/v1/goalhospital/uploadcsv`, formData, {
         observe: 'response',
      })
   }

   geGoalStatusFilterData(data:any) {
      return this.http.get(`${this.API_BASE_URL}/v1/lookups?types=${data}`)
   }


   cretateTask(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/ltktask/createtask`, data)
   }

   /** @deprecated superseded by bulkCreateTaskNew (v1/ltktask/bulkcreatetask-new) */
   bulkCreateTask(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/ltktask/bulkcreatetask`, data)
   }

   /**
    * Bulk-creates one task per (hospital × resolved assignee). Payload:
    * { practiceSKeys, goalDefinitionId, assigneeIds, assignToRoles, taskName, taskDescription,
    *   dueDate, statusId, categoryId, quarterName, isCritical }
    */
   bulkCreateTaskNew(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/ltktask/bulkcreatetask-new`, data)
   }

   private currentUserInfoCache: any;
   getCurrentUserInfo() {
      if (this.currentUserInfoCache) {
         return new BehaviorSubject(this.currentUserInfoCache).asObservable();
      }
      return this.http.get(`${this.API_BASE_URL}/v1/user/current-info`).pipe(
         tap((res: any) => {
            this.currentUserInfoCache = res;
         })
      );
   }
   
   updateTask(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/ltktask/updatetask`, data);
   }

   deleteTask(taskId: any, reason?: string) {
      const params = reason ? `?comments=${encodeURIComponent(reason)}` : '';
      return this.http.delete(`${this.API_BASE_URL}/v1/ltktask/deletetask/${taskId}${params}`);
   }

   createnotesbymeeting(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/schedule/createnotesbymeeting`, data)
   }
   getnotesbymeetingid(meetingId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/getnotesbymeetingid?meetingId=${meetingId}`);
   }
   updatenotesbymeeting(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/schedule/updatenotesbymeeting`, data);
   }
   deletenotesbymeeting(data: any) {
      return this.http.request('DELETE', `${this.API_BASE_URL}/v1/schedule/deletenotesbymeeting`, { body: data });
   }

   deleteMeeting(meetingId: any) {
      return this.http.delete(`${this.API_BASE_URL}/v1/schedule/deleteschedulemeeting/${meetingId}`);
   }

   deleteSeries(meetingId: number, deleteEntireSeries: boolean) {
      return this.http.delete(`${this.API_BASE_URL}/v1/schedule/deleteseries/${meetingId}?deleteEntireSeries=${deleteEntireSeries}`);
   }

   getSeriesOccurrences(meetingId: number, rangeStart: string, rangeEnd: string) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/series-occurrences/${meetingId}?rangeStart=${rangeStart}&rangeEnd=${rangeEnd}`);
   }

   createTeamsMeeting(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/schedule/createteamsmeeting`, data);
   }

   updateMeeting(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/schedule/updatemeeting`, data);
   }

   getTranscripts(meetingId: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/gettranscripts/${meetingId}`);
   }

   getRecordings(meetingId: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/getrecordings/${meetingId}`);
   }

   getAiNotes(meetingId: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/getainotes/${meetingId}`);
   }

   getMeetingTranscriptStatus(meetingId: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/meetingtranscriptstatus/${meetingId}`);
   }

    triggerTranscriptFetch(meetingId: number) {
       return this.http.post(`${this.API_BASE_URL}/v1/schedule/trigger-transcript-fetch/${meetingId}`, {});
    }

    fetchAiNotes(meetingId: number) {
       return this.http.post(`${this.API_BASE_URL}/v1/schedule/fetchainotes/${meetingId}`, {});
    }

   downloadRecording(recordingId: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/recordings/${recordingId}/download`, {
         responseType: 'blob',
      });
   }

   getOccurrenceAssets(occurrenceCacheId: number) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/occurrence-assets/${occurrenceCacheId}`);
   }

   triggerOccurrenceFetch(occurrenceCacheId: number) {
      return this.http.post(`${this.API_BASE_URL}/v1/schedule/trigger-occurrence-fetch/${occurrenceCacheId}`, {});
   }

   getOccurrenceCache(seriesMasterId: number, occurrenceDate: string) {
      return this.http.get(`${this.API_BASE_URL}/v1/schedule/occurrence-cache?seriesMasterId=${seriesMasterId}&occurrenceDate=${occurrenceDate}`);
   }

   updateOccurrenceTime(meetingId: number, occurrenceDate: string, startTime: string, endTime: string) {
      return this.http.put(`${this.API_BASE_URL}/v1/schedule/series/${meetingId}/occurrence/${occurrenceDate}`, { startTime, endTime });
   }

   cancelOccurrence(meetingId: number, occurrenceDate: string) {
      return this.http.delete(`${this.API_BASE_URL}/v1/schedule/series/${meetingId}/occurrence/${occurrenceDate}`);
   }

   goalTargetAssignment(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/goalhospital/goaltarget`, data);
   }
   updateGoalStatus(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/goalhospital/updategoalstatus`, data);
   }
   updateGoalTargetStatus(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/goaltarget/updatestatus`, data);
   }
   updateRecommendedTarget(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/goaltarget/updategoaltarget`, data);
   }
   getApprovalHistory(targetId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/goaltarget/${targetId}/approvalhistory`);
   }

   deleteGoalTarget(body: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/goaltarget/deletegoaltarget`, body);
   }

   updateGoalDictionary(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/goalhospital/goal-dictionary`, data);
   }

   deleteGoalDictionary(goalId: number) {
      return this.http.delete(`${this.API_BASE_URL}/v1/ltktask/goal-dictionary/${goalId}`);
   }

   processGoalApproval(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/goaltarget/processgoalapproval`, data);
   }
   updateGoalTargetIsHm(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/goaltarget/updateishm`, data);
   }
   getTasksDashboardData(qtr: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/ltkdashboard/gettasksdashboard?qtr=${qtr}`);
   }

   getNotifications(){
      return this.http.get(`${this.API_BASE_URL}/v1/ltknotification/getnotifications`);
   }
   updateNotificationAsRead(notificationId: any) {
      return this.http.patch(`${this.API_BASE_URL}/v1/ltknotification/markasreadnotification?notificationId=${notificationId}`, null);
   }

   ltkUserList() {
      return this.http.get(`${this.API_BASE_URL}/v1/user/ltk-users`);
   }
   eligibleUserList() {
      return this.http.get(`${this.API_BASE_URL}/v1/user/eligible-users`);
   }
   manageUserAccess(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/user/manage-access`, data);
   }

   upsertPracticeUser(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/goalhospital/upsert-practice-user`, data);
   }

   deletePracticeUser(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/goalhospital/delete-practice-user`, data);
   }

   getHospitalsByUserOverride(userId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/goalhospital/hospitals-by-user-override/${userId}`);
   }

   createGoalComment(data: any) {
      return this.http.post(`${this.API_BASE_URL}/v1/goaltarget/creategoalcomment`, data);
   }
   getGoalComments(goalTargetId: any) {
      return this.http.get(`${this.API_BASE_URL}/v1/goaltarget/getgoalcomment?goalTargetId=${goalTargetId}`);
   }
   updateGoalComment(data: any) {
      return this.http.put(`${this.API_BASE_URL}/v1/goaltarget/updategoalcomment`, data);
   }
   deleteGoalComment(data: any) {
      return this.http.request('DELETE', `${this.API_BASE_URL}/v1/goaltarget/deletegoalcomment`, { body: data });
   }
   hasAccessToManageUsersLtk() {
      return this.http.get(`${this.API_BASE_URL}/v1/user/hasltkaccess`);
   }
getOpsHospitalData(qtr: any, hospitalId?: number) {
       const queryParams = new URLSearchParams();
       queryParams.set('statusIds', '14,18');
       if (hospitalId != null) {
          queryParams.set('hospitalId', hospitalId.toString());
       }
       const params = queryParams.toString() ? `?${queryParams.toString()}` : '';
       return this.http.get(`${this.API_BASE_URL}/v1/ltkdashboard/ops-hospital${this.getQuarterPathSegment(qtr)}${params}`);
    }

    getHospitalDashboardData(hospitalId: number, quarter?: string, viewAsHm: boolean = false) {
       const params = new URLSearchParams();
       params.set('hospitalId', hospitalId.toString());
       if (quarter && quarter.trim() && quarter.toLowerCase() !== 'default') {
          params.set('quarter', quarter);
       }
       params.set('statusIds', '14,18');
       if (viewAsHm) {
          params.set('viewAsHm', 'true');
       }
       return this.http.get(`${this.API_BASE_URL}/v1/hospitaldashboard/hospital-summary?${params.toString()}`);
    }

    getHospitalDashboardTrajectory(goalTargetId: number) {
       return this.http.get(`${this.API_BASE_URL}/v1/hospitaldashboard/goal-trajectory?goalTargetId=${goalTargetId}`);
    }

   private getQuarterPathSegment(qtr?: any): string {
      const normalizedQuarter = (qtr ?? '').toString().trim();
      if (!normalizedQuarter || normalizedQuarter.toLowerCase() === 'default') {
         return '';
      }
      return `/${encodeURIComponent(normalizedQuarter)}`;
   }

   // Cache clearing methods
   clearGoalListCache() {
      this.goalListCache = null;
   }


   clearStatusListCache() {
      this.statusListCache = null;
   }

   clearTaskListCache() {
      this.taskListCache = null;
   }

   clearOpsHospitalDataCache(qtr?: any) {
      if (qtr === undefined || qtr === null) {
         this.opsHospitalDataCache = {};
         return;
      }

      const cacheKey = `${qtr}`;
      delete this.opsHospitalDataCache[cacheKey];
   }

   // Clear all caches
   clearAllCaches() {
      this.goalListCache = null;
      this.hospitalListCache = null;
      this.statusListCache = null;
      this.taskListCache = null;
      this.opsHospitalDataCache = {};
   }

   // Snackbar notification method
   openSnackBar(message: string, action: string, duration: number = 5000) {
      if (this.currentSnackBar) {
         this.currentSnackBar.dismiss();
      }
      this.currentSnackBar = this.snackBar.open(message, action, {
         duration: duration,
         horizontalPosition: 'end',
         verticalPosition: 'top',
         panelClass: ['custom-info-snackbar'],
         politeness: 'polite',
      });
   }

   showNotification(message: string, isError: boolean) {
      if (this.currentSnackBar) {
         this.currentSnackBar.dismiss();
      }
      this.currentSnackBar = this.snackBar.open(message, 'Close', {
         duration: 5000,
         horizontalPosition: 'end',
         verticalPosition: 'top',
         panelClass: isError ? ['custom-error-snackbar'] : ['custom-success-snackbar'],
         politeness: 'polite',
      });
   }

  notificationEvent = new BehaviorSubject(null);
   triggerNotificationEvent(data: any) {
      this.notificationEvent.next(data);
   }

   opsEventUpdate = new BehaviorSubject(false);
   triggerOpsEventUpdate(data: any) {
      this.opsEventUpdate.next(data);
   }

   searchHighlightEvent = new Subject<{ module: string; id: number }>();
   triggerSearchHighlight(module: string, id: number) {
      this.searchHighlightEvent.next({ module, id });
   }

   // Global Search Method
   globalSearch(query: string, page: number = 1) {
      return this.http.get(`${this.API_BASE_URL}/v1/api/search?q=${encodeURIComponent(query)}&page=${page}`);
   }

}
